/**
 * AgentKnowledge — Agents → <agent> → Knowledge (mockups/kiaan-knowledge.html, screen 3). Admin only.
 * A checkbox tree of containers › branches with a "partial" box. Ticking a container ticks every
 * branch under it (their own rows go, so they follow the container again); a branch can then be
 * switched on its own. Save sends only what changed. An agent never reads more than the person
 * who asks it — the server enforces that, this screen only says so.
 */
import './knowledge.css';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { createAgentThread, kiaanErrorText } from '../kiaanApi';
import { getAgentAccess, knowledgeErrorText, knowledgeLoadErrorText, setAgentAccess, type AccessState, type AgentAccessNode } from './knowledgeApi';

type Own = Record<string, boolean | null>;

function flatten(tree: AgentAccessNode[]): AgentAccessNode[] {
  return tree.flatMap((n) => [n, ...flatten(n.children ?? [])]);
}

/** On/off of one group: its own row, else its parent's, else off. */
function allowed(key: string, own: Own, parentOf: Map<string, string | null>): boolean {
  for (let k: string | null | undefined = key; k; k = parentOf.get(k)) {
    const v = own[k];
    if (v === true || v === false) return v;
  }
  return false;
}

/** Box state from the own rows (pure; the tests pin it). */
export function boxState(node: AgentAccessNode, own: Own, parentOf: Map<string, string | null>): AccessState {
  const all = [node, ...flatten(node.children ?? [])];
  const on = all.filter((n) => allowed(n.key, own, parentOf)).length;
  // A container with branches: the container itself is just the default for them, so count the branches.
  const kids = flatten(node.children ?? []);
  if (kids.length) {
    const k = kids.filter((n) => allowed(n.key, own, parentOf)).length;
    return k === 0 ? 'off' : k === kids.length ? 'on' : 'partial';
  }
  return on ? 'on' : 'off';
}

export function AgentKnowledge({ agentId, agentName }: { agentId: string; agentName: string }) {
  const [tree, setTree] = useState<AgentAccessNode[] | null>(null);
  const [orig, setOrig] = useState<Own>({});
  const [own, setOwn] = useState<Own>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [question, setQuestion] = useState<string | null>(null);
  const [asked, setAsked] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setTree(null);
    getAgentAccess(agentId)
      .then((t) => {
        if (!alive) return;
        const o: Own = {};
        for (const n of flatten(t)) o[n.key] = n.own ?? null;
        setTree(t);
        setOrig(o);
        setOwn(o);
        setError(null);
      })
      .catch((e) => { if (alive) setError(knowledgeLoadErrorText(e)); });
    return () => { alive = false; };
  }, [agentId, tick]);

  const flat = useMemo(() => flatten(tree ?? []), [tree]);
  const parentOf = useMemo(() => new Map(flat.map((n) => [n.key, n.parentKey])), [flat]);
  const changed = flat.filter((n) => (own[n.key] ?? null) !== (orig[n.key] ?? null));

  const toggle = (node: AgentAccessNode) => {
    const next = boxState(node, own, parentOf) !== 'on';
    setSaved(null);
    setOwn((o) => {
      const n: Own = { ...o, [node.key]: next };
      for (const d of flatten(node.children ?? [])) n[d.key] = null;
      // A branch switched on its own: if its parent already gives the same answer, drop the row.
      const p = node.parentKey;
      if (p && allowed(p, n, parentOf) === next) n[node.key] = null;
      return n;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Parents first so a branch row is written against the new container default.
      for (const n of changed) await setAgentAccess(agentId, n.key, own[n.key] ?? null);
      setSaved(`Saved ${changed.length} ${changed.length === 1 ? 'change' : 'changes'}. ${agentName} uses them from its next run.`);
      setTick((t) => t + 1);
    } catch (e) {
      setError(knowledgeErrorText(e));
    } finally {
      setSaving(false);
    }
  };

  const ask = async () => {
    const q = question?.trim();
    if (!q) return;
    setError(null);
    try {
      await createAgentThread(agentId, q, 'Knowledge test');
      setAsked(`Asked ${agentName} in a new chat “Knowledge test”. Its answer shows in its chats.`);
      setQuestion(null);
    } catch (e) {
      setError(kiaanErrorText(e));
    }
  };

  if (!tree) return error ? <div className="kw-banner" role="status">{error}</div> : <div className="kw-small" style={{ padding: 12 }}>Loading…</div>;

  const row = (n: AgentAccessNode, depth: number): ReactElement => {
    const st = boxState(n, own, parentOf);
    const hasKids = (n.children ?? []).length > 0;
    return (
      <div key={n.key}>
        <label className="kn-ti" style={{ paddingLeft: 6 + depth * 18 }} data-testid={`ak-${n.key}`} data-state={st}>
          <input type="checkbox" className="kn-chkin" checked={st === 'on'} aria-checked={st === 'partial' ? 'mixed' : st === 'on'}
            ref={(el) => { if (el) el.indeterminate = st === 'partial'; }} onChange={() => toggle(n)} disabled={saving} aria-label={n.label} />
          <span className={`kn-chk${st === 'on' ? ' on' : st === 'partial' ? ' half' : ''}`} aria-hidden />
          {depth === 0 ? <b>{n.label}</b> : n.label}
          {depth === 0 && hasKids && st === 'on' && <span className="kw-small">&nbsp;(all branches)</span>}
        </label>
        {(n.children ?? []).map((c) => row(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="kn-pane kn-agent" data-testid="agent-knowledge">
      <div className="kn-row"><b className="kn-title">What {agentName} may read</b><span className="kw-small" style={{ marginLeft: 'auto' }}>changes apply from its next run</span></div>
      {tree.length === 0 && <div className="kw-small">No knowledge yet. Add some in Dev mode → Knowledge.</div>}
      {tree.length > 0 && !flat.some((n) => allowed(n.key, own, parentOf)) && (
        <div className="kw-banner" role="note" data-testid="ak-none">No knowledge given — this agent sees nothing</div>
      )}
      <div className="kn-tree">{tree.map((n) => row(n, 0))}</div>
      <div className="kn-card">An agent never sees more than the person who asks it. If a sales rep chats with {agentName}, {agentName} reads only what both are allowed to read.</div>
      {error && <div className="kw-banner kw-danger" role="alert">{error}</div>}
      {saved && <div className="kw-banner kw-ok" role="status">{saved}</div>}
      {asked && <div className="kw-banner kw-ok" role="status">{asked}</div>}
      <div className="kn-row">
        <button type="button" className="kw-btn" disabled={saving || changed.length === 0} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</button>
        {changed.length > 0 && <button type="button" className="kw-btn kw-light" disabled={saving} onClick={() => { setOwn(orig); setSaved(null); }}>Undo changes</button>}
        {question === null && <button type="button" className="kw-btn kw-light" onClick={() => { setQuestion(''); setAsked(null); }}>Preview: ask {agentName} a test question</button>}
      </div>
      {question !== null && (
        <span className="kn-inline">
          <input className="kw-fld" aria-label="Test question" placeholder="e.g. What is the MRP of the 6 kW heat pump?" value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void ask(); }} />
          <button type="button" className="kw-btn" disabled={!question.trim() || changed.length > 0} title={changed.length > 0 ? 'Save first' : undefined} onClick={() => void ask()}>Ask</button>
          <button type="button" className="kw-btn kw-light" onClick={() => setQuestion(null)}>Cancel</button>
        </span>
      )}
    </div>
  );
}
